import { logger } from './logger';

type Handler<Payload> = (payload: Payload) => void;

type Subscription<Payload> = {
  recipient?: string | undefined;
  handler: Handler<Payload>;
};

export abstract class AbstractEmitter<Event extends string = string, Payload = any> {
  private listeners = new Map<Event, Subscription<Payload>[]>();

  on(event: Event, handler: Handler<Payload>, recipient?: string | undefined) {
    const subs = this.listeners.get(event) ?? [];

    subs.push({
      handler,
      recipient,
    });

    this.listeners.set(event, subs);

    return () => this.off(event, handler);
  }

  off(event: Event, handler: Handler<Payload>) {
    const subs = this.listeners.get(event);
    if (!subs) return;

    const filteredSubs = subs.filter((s) => s.handler !== handler);
    this.listeners.set(event, filteredSubs);
  }

  // broadcast to all listeners
  protected emit(event: Event, payload: Payload) {
    this.listeners.get(event)?.forEach((s) => {
      try {
        s.handler(payload);
      } catch (error: any) {
        logger.error(`Error in handler for event "${event}":`, error);
      }
    });
  }

  // targeted sending with recipients without identifier
  protected emitTo(recipient: string, event: Event, payload: Payload) {
    this.listeners.get(event)?.forEach((s) => {
      // TODO: ONLY WHILE WE USING OLD SOCKET LOGIC
      if (s.recipient === recipient || !s.recipient) {
        try {
          s.handler(payload);
        } catch (error: any) {
          logger.error(
            `Error in handler for event "${event}" with recipient "${recipient}":`,
            error,
          );
        }
      }
    });
  }

  protected offRecipient(recipient: string) {
    for (const [event, subs] of this.listeners.entries()) {
      const filteredSubs = subs.filter((s) => s.recipient !== recipient);

      this.listeners.set(event, filteredSubs);
    }
  }

  emitterScope(recipient: string) {
    return {
      emitTo: (event: Event, payload: Payload) => this.emitTo(recipient, event, payload),
      off: (event: Event, handler: Handler<Payload>) => this.off(event, handler),
      on: (event: Event, handler: Handler<Payload>) => this.on(event, handler, recipient),
    };
  }
}
